import React, { useState } from 'react';
import { ArrowRight, MessageCircle, Calendar, Star } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalendar from './BookingCalendar';

const CHAMP_IMAGES = [
  {
    src: 'https://customer-assets.emergentagent.com/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/kjn9rg2u_Screenshot_20260408_152219_Instagram.jpg',
    alt: 'Irish W40 4x200m Relay Squad - Silver Medal, Torun Poland',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/qmrgzoop_Screenshot_20260408_145607_Instagram.jpg',
    alt: 'Irish relay team at European Masters Athletics Championships',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/bil9b56u_Screenshot_20260408_145614_Instagram.jpg',
    alt: 'Irish athlete celebrating with flag at European Masters',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/v015vux5_Screenshot_20260408_145640_Instagram.jpg',
    alt: '4x200 European Silver medalist',
  },
];

// Promo auto-expiry: hide after May 4, 2026 (4 weeks from launch)
const PROMO_EXPIRY = new Date('2026-06-01T23:59:59');

const SHARE_MSG = `\u{1F3C6}\u{1F1EE}\u{1F1EA} Calling all Irish Masters Athletes! \u{1F3C3}\u{200D}\u{2640}\u{FE0F}\u{1F3C3}\n\nJust back from the European Masters Athletics Championships in Torun, Poland? \u{1F1F5}\u{1F1F1}\n\nTime to RECOVER! \u{1F4AA} Get a massage at Dublin Holistic Massage and add ATHLETE10 in your booking notes for \u{20AC}10 OFF! \u{1F389}\n\nBest therapists available Thu, Fri & Sun: Buggie, Eegi & Dr. Michael Omakobia \u{2B50}\n\nBook: www.dublinholisticmassages.com\nWhatsApp: +353857747474\n\nValid for you, friends & family! \u{2764}\u{FE0F}\nLet's get ready for World Masters in South Korea! \u{1F1F0}\u{1F1F7}\u{1F525}`;

const MastersAthleticsPromo = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  // Auto-expiry: don't render after expiry date
  if (new Date() > PROMO_EXPIRY) return null;

  return (
    <>
      <section className="py-20 relative overflow-hidden" data-testid="masters-athletics-promo"
        style={{ background: 'linear-gradient(135deg, #0A1F19 0%, #16382E 30%, #1C473A 60%, #0F2922 100%)' }}>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-5"
          style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'2\' fill=\'%23D4AF37\'/%3E%3C/svg%3E")'}} />
        <div className="absolute top-10 right-10 text-6xl opacity-20 select-none">{'\u{1F3C6}'}</div>
        <div className="absolute bottom-10 left-10 text-5xl opacity-15 select-none">{'\u{1F1EE}\u{1F1EA}'}</div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-luxury-gold/20 text-luxury-gold px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-6 border border-luxury-gold/30">
              <Star size={14} className="fill-luxury-gold" />
              {'\u{1F3C6}'} European Masters Athletics {'\u{1F3C6}'}
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mb-4 leading-tight">
              {'\u{1F1EE}\u{1F1EA}'} Well Done Ireland! {'\u{1F1EE}\u{1F1EA}'}
              <span className="block text-luxury-gold italic mt-2 text-2xl sm:text-3xl lg:text-4xl">
                European Masters Athletics Championships {'\u{1F3C5}'}
              </span>
              <span className="block text-white/70 text-lg sm:text-xl mt-2 font-light">
                Torun, Poland {'\u{1F1F5}\u{1F1F1}'}
              </span>
            </h2>

            <p className="text-white/80 font-light max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
              {'\u{1F44F}'} Congratulations to everyone competing, travelling, and to the amazing home supporters — you ALL did a brilliant job! {'\u{2764}\u{FE0F}\u{1F525}'}
            </p>
          </div>

          {/* Photo Gallery */}
          <div className="mb-12">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-2xl mx-auto border-2 border-luxury-gold/30">
              <img
                src={CHAMP_IMAGES[activeImg].src}
                alt={CHAMP_IMAGES[activeImg].alt}
                className="w-full h-[350px] sm:h-[450px] object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <p className="text-white text-sm font-medium">{'\u{1F4F8}'} {CHAMP_IMAGES[activeImg].alt}</p>
              </div>
            </div>
            <div className="flex gap-3 justify-center mt-4">
              {CHAMP_IMAGES.map((img, i) => (
                <button
                  key={img.src}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                    i === activeImg ? 'border-luxury-gold scale-105 shadow-lg' : 'border-white/20 opacity-60 hover:opacity-90'
                  }`}
                  data-testid={`champ-photo-${i}`}
                >
                  <img src={img.src} alt="" className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          </div>

          {/* Recovery Offer */}
          <div className="grid lg:grid-cols-2 gap-8 items-start">

            {/* Left: The Message */}
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-white/20">
              <h3 className="font-display text-2xl text-luxury-gold mb-4">
                {'\u{1F4AA}'} Recovery Time is NOW! {'\u{1F6C1}'}
              </h3>

              <p className="text-white/90 mb-4 leading-relaxed">
                The best speedy recovery after competition is <strong className="text-luxury-gold">MASSAGE</strong>! {'\u{2728}'}
                Your muscles worked hard — now give them the care they deserve.
              </p>

              <div className="bg-white/10 rounded-2xl p-4 mb-4 border border-luxury-gold/20">
                <p className="text-luxury-gold font-semibold mb-2">{'\u{1F4C5}'} Best Days to Book:</p>
                <div className="flex flex-wrap gap-2">
                  {['Thursday', 'Friday', 'Sunday'].map(day => (
                    <span key={day} className="bg-luxury-gold/20 text-luxury-gold px-3 py-1.5 rounded-full text-sm font-medium border border-luxury-gold/30">
                      {day} {'\u{2705}'}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-white/10 rounded-2xl p-4 mb-4 border border-luxury-gold/20">
                <p className="text-luxury-gold font-semibold mb-2">{'\u{2B50}'} Recommended Therapists:</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <span className="text-lg">{'\u{1F947}'}</span>
                    <span><strong>Buggie</strong> — Exceptional Deep Tissue & Relaxing</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <span className="text-lg">{'\u{1F947}'}</span>
                    <span><strong>Eegi</strong> — Outstanding Thai, Shiatsu & Deep Tissue</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <span className="text-lg">{'\u{1F947}'}</span>
                    <span><strong>Dr. Michael Omakobia</strong> — Masters Athlete & Lead Therapist</span>
                  </div>
                </div>
                <p className="text-white/60 text-xs mt-2 italic">
                  Therapists from Mongolia {'\u{1F1F2}\u{1F1F3}'} — the rest of the team is also great, book any day! {'\u{1F60A}'}
                </p>
                <div className="mt-3 flex items-center gap-2 text-white/80 text-sm bg-white/10 rounded-xl px-3 py-2 border border-luxury-gold/20">
                  <span className="text-lg">{'\u{1F4CD}'}</span>
                  <span>Athletes can come to our <strong className="text-luxury-gold">Clarehall</strong> location — free parking & shower facilities! {'\u{1F3CB}\u{FE0F}\u{2728}'}</span>
                </div>
              </div>

              <p className="text-white/80 text-sm">
                {'\u{1F46A}'} Offer valid for <strong className="text-white">you, your friends and family members</strong>!
              </p>
            </div>

            {/* Right: The Deal */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-luxury-gold/20 to-luxury-gold/5 rounded-3xl p-6 sm:p-8 border border-luxury-gold/40 text-center">
                <div className="text-5xl mb-3">{'\u{1F3C3}\u{200D}\u{2642}\u{FE0F}\u{1F3C3}\u{200D}\u{2640}\u{FE0F}'}</div>
                <h3 className="font-display text-3xl text-white mb-1">ATHLETE10</h3>
                <p className="text-luxury-gold text-xl font-bold mb-3">{'\u{20AC}'}10 OFF Your Massage! {'\u{1F389}'}</p>

                <div className="bg-white/10 rounded-xl p-4 mb-4 text-left">
                  <p className="text-white/90 text-sm mb-2">{'\u{1F4DD}'} <strong>How to claim:</strong></p>
                  <ol className="text-white/80 text-sm space-y-1.5 list-decimal list-inside">
                    <li>Book online at <strong className="text-luxury-gold">www.dublinholisticmassages.com</strong> {'\u{1F4BB}'}</li>
                    <li>Add <strong className="text-luxury-gold">ATHLETE10</strong> in the booking notes {'\u{270D}\u{FE0F}'}</li>
                    <li>Mention how you know Dr. Michael or Ana {'\u{1F91D}'}</li>
                    <li>Enjoy already discounted online prices! {'\u{1F4B0}'}</li>
                  </ol>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    onClick={() => setBookingOpen(true)}
                    className="w-full bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green rounded-full py-6 text-sm uppercase tracking-wider font-semibold group"
                    data-testid="masters-book-btn"
                  >
                    {'\u{1F4AA}'} Book Your Recovery Massage
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>

                  <Button
                    onClick={() => window.open('https://wa.me/353857747474?text=' + encodeURIComponent('\u{1F3C6} Hi! I\'m an Irish Masters Athlete and I\'d like to book a recovery massage. ATHLETE10'), '_blank')}
                    className="w-full bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full py-5 text-sm font-medium"
                    data-testid="masters-whatsapp-book"
                  >
                    <MessageCircle size={18} className="mr-2" />
                    WhatsApp +353 85 774 7474
                  </Button>
                </div>
              </div>

              {/* Share CTA */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20 text-center">
                <p className="text-white/80 text-sm mb-3">
                  {'\u{1F4E3}'} Share with your teammates & training partners!
                </p>
                <Button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_MSG)}`, '_blank')}
                  className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full px-6 py-3 text-sm"
                  data-testid="masters-whatsapp-share"
                >
                  <MessageCircle size={16} className="mr-2" />
                  Share This Offer {'\u{1F4AC}'}
                </Button>
              </div>

              {/* World Masters Teaser */}
              <div className="bg-gradient-to-r from-blue-900/40 to-red-900/40 rounded-2xl p-5 border border-white/20 text-center">
                <p className="text-2xl mb-2">{'\u{1F1F0}\u{1F1F7}\u{1F30F}'}</p>
                <p className="text-white font-display text-lg">
                  Let's Get Ready for <span className="text-luxury-gold">World Masters Athletics</span>
                </p>
                <p className="text-white/70 text-sm">
                  South Korea {'\u{1F1F0}\u{1F1F7}'} — Stay strong, stay recovered! {'\u{1F4AA}\u{1F525}'}
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      <BookingCalendar isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </>
  );
};

export default MastersAthleticsPromo;
